import { IScaleOptionDefinition, IScaleSelection } from '../store/vitals.types'

/** Returns the total score for a single scale section, or null if nothing is selected. */
export function scaleSectionTotal(scale: IScaleOptionDefinition, selections: IScaleSelection[]): number | null {
  const picked = selections.filter((s) => s.scaleId === scale.scaleId)
  if (!picked.length) return null
  return picked.reduce((sum, s) => sum + (s.score ?? 0), 0)
}

/** Returns combined totals across all scales as a comma-separated string, and a display-ready button score. */
export function scaleCombinedTotals(
  selections: IScaleSelection[],
  scales: IScaleOptionDefinition[]
): { totalsCombined: string; buttonScore: string } {
  const totals = scales.map((s) => scaleSectionTotal(s, selections)).filter((t): t is number => t !== null)

  const totalsCombined = totals.length ? totals.join(',') : ''
  const buttonScore = totals.length ? totals.join(',') : 'N/A'

  return { totalsCombined, buttonScore }
}

function findCommentBlockParts(
  comments: string,
  prefix: string
): { before: string; after: string; separator: string } | null {
  const startIndex = comments.indexOf(prefix)
  if (startIndex < 0) return null

  const endIndex = comments.indexOf(';', startIndex)
  const blockEnd = endIndex < 0 ? comments.length : endIndex + 1

  const charBeforeBlock = startIndex > 0 ? comments[startIndex - 1] : '\n'

  return {
    before: comments.slice(0, startIndex).trimEnd(),
    after: comments.slice(blockEnd),
    separator: charBeforeBlock === '\n' || charBeforeBlock === '\r' ? '\n' : ' ',
  }
}

/** Strips out the prefixed block from comments, preserving all surrounding text. */
export function removeCommentBlock(comments: string, prefix: string): string {
  const slicedBlocked = findCommentBlockParts(comments ?? '', prefix)
  if (!slicedBlocked) return (comments ?? '').trim()
  const { before, after } = slicedBlocked
  return [before, after.trimStart()].filter(Boolean).join('\n').trim()
}

/** Updates the prefixed block in comments if it exists, or appends it if not. */
export function updateCommentBlock(comments: string, newLine: string, prefix: string): string {
  const current = comments ?? ''
  const slicedBlocked = findCommentBlockParts(current, prefix)

  if (slicedBlocked) {
    const { before, after, separator } = slicedBlocked
    if (!newLine) return [before, after.trimStart()].filter(Boolean).join('\n').trim()
    const base = before ? `${before}${separator}${newLine}` : newLine
    return (base + after).trim()
  }

  if (!newLine) return current.trim()
  const trimmed = current.trim()
  return trimmed ? `${trimmed}\n${newLine}` : newLine
}

/** Removes withdrawal scale comment block by matching known scale names: "COWS Score: ...;" or "CIWA Score: ...;" */
export function removeWithdrawalScaleCommentBlock(comments: string, withdrawalScaleNames: string[] = []): string {
  if (!withdrawalScaleNames.length) {
    return comments
  }
  const scaleNamePattern = withdrawalScaleNames
    .map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+'))
    .join('|')
  const pattern = new RegExp(`(?:${scaleNamePattern})\\s+Score:\\s*(?:[\\s\\S]*?Total:\\s*[^;]+;|[\\s\\S]*?;)`, 'g')
  const updated = comments.replace(pattern, '').trim()
  return updated
}
